import { useTranslation } from 'react-i18next';

import type { UserAdminBatchConfirmCard, UserAdminBatchItem } from '@bonusly/bizy-kit';
import { Box, Text } from '@bonusly/design-system';

import { humanizeFieldToken, UserAdminCard, UserAdminChangeRow, UserAdminFieldRow } from './lib/user-admin-card';

/**
 * Confirmation card for a BATCH user-admin action (INS-1011): inviting,
 * updating, deactivating, or reactivating more than one user at once. One
 * card represents the whole batch, so the admin confirms a single time and
 * Confirm replays the action with `confirmed=true` for exactly the users
 * listed here. A single renderer covers every action type — each item just
 * carries generic `rows` (invite fields) and/or `changes` (update diffs).
 */
export function UserAdminBatchConfirmCardView({
	block,
	fontSize = 'sm',
	isReadOnly = false,
}: {
	block: UserAdminBatchConfirmCard;
	fontSize?: 'sm' | 'md';
	isReadOnly?: boolean;
}) {
	const { t } = useTranslation('admin_bizy');
	const { action, title, consequence, items, skipped, actions } = block.payload;

	// Prefer the localized title (built from action + count); fall back to
	// the backend-provided `title`, then a generic localized count.
	const heading = t(`uiBlocks.userAdmin.batchTitle.${action}`, {
		count: items.length,
		defaultValue: title || t('uiBlocks.userAdmin.batchTitleFallback', { count: items.length }),
	});

	return (
		<UserAdminCard title={heading} subtitle={consequence || undefined} actions={actions} isReadOnly={isReadOnly} fontSize={fontSize}>
			<Box display="flex" flexDirection="column" gap={2}>
				{items.map((item, index) => (
					<BatchItemRow key={`${item.title}-${index}`} item={item} fontSize={fontSize} />
				))}
			</Box>

			{skipped && skipped.length > 0 && (
				<Box mt={2} pt={2} borderTop="1px solid" borderColor="border.soft">
					<Text fontSize="xs" color="text.softer" fontWeight="medium">
						{t('uiBlocks.userAdmin.skippedHeading', { count: skipped.length })}
					</Text>
					{skipped.map((entry, index) => (
						<Text key={`${entry.label}-${index}`} fontSize="xs" color="text.softer">
							{entry.reason ? `${entry.label} — ${entry.reason}` : entry.label}
						</Text>
					))}
				</Box>
			)}
		</UserAdminCard>
	);
}

function BatchItemRow({ item, fontSize }: { item: UserAdminBatchItem; fontSize: 'sm' | 'md' }) {
	const rows = item.rows ?? [];
	const changes = item.changes ?? [];

	return (
		<Box minW={0}>
			<Text fontSize={fontSize} fontWeight="medium" color="text.default" isTruncated title={item.title}>
				{item.title}
			</Text>
			{item.subtitle && (
				<Text fontSize="xs" color="text.softer" isTruncated title={item.subtitle}>
					{item.subtitle}
				</Text>
			)}
			{rows.map((row, index) => (
				<UserAdminFieldRow key={`${row.label}-${index}`} label={row.label} value={row.value || '—'} fontSize={fontSize} />
			))}
			{changes.map((change, index) => (
				<UserAdminChangeRow
					key={`${change.field}-${index}`}
					label={humanizeFieldToken(change.field)}
					before={change.before || '—'}
					after={change.after || '—'}
					fontSize={fontSize}
				/>
			))}
		</Box>
	);
}
